import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Icon } from '@/components/icon/index.js';
import type { IconName } from '@/components/icon/index.js';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/utils.js';
import { resolveAvatarSrc } from '../../lib/avatar.js';
import { SUPPORTED_LANGUAGES, setLanguage, type SupportedLanguage } from '../../i18n.js';
import { useAuthStore } from '../../store/authStore.js';
import { useLandingSectionNav } from '../../hooks/useLandingSectionNav.js';
import { landingPathToSectionId } from '../../lib/landingScroll.js';
import { GatorMark } from './GatorMark.js';
import { MobileNavMenu } from './MobileNavMenu.js';
import { MOBILE_NAV_LOGO_TEXT, MobileNavToggleButton } from './mobileNavChrome.js';

export type NavBarProps = {
  variant: 'landing' | 'app';
  borderless?: boolean;
  appBorderClassName?: string;
  hideGuestAuthButtons?: boolean;
  showGuestLanguageButton?: boolean;
  /**
   * Minimal chrome for gated states (e.g. the email-activation page): hides the
   * Dashboard shortcut and the app menu items, leaving only Language + Sign Out.
   */
  restricted?: boolean;
};

export function NavBar({
  variant,
  borderless = false,
  appBorderClassName,
  hideGuestAuthButtons = false,
  showGuestLanguageButton = false,
  restricted = false,
}: NavBarProps) {
  const { t, i18n } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const signOut = useAuthStore((s) => s.signOut);
  const setDisplayLanguage = useAuthStore((s) => s.setDisplayLanguage);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const showDashboardLink = Boolean(user) && pathname !== '/dashboard' && !restricted;
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [desktopMenuOpen, setDesktopMenuOpen] = useState(false);
  const [desktopLangOpen, setDesktopLangOpen] = useState(false);
  const [guestLangOpen, setGuestLangOpen] = useState(false);
  const [landingScrolled, setLandingScrolled] = useState(false);
  const desktopMenuRef = useRef<HTMLDivElement | null>(null);
  const guestLangRef = useRef<HTMLDivElement | null>(null);
  const headerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!mobileMenuOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [mobileMenuOpen]);

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth >= 1024) {
        setMobileMenuOpen(false);
      }
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!desktopMenuOpen) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (desktopMenuRef.current?.contains(target)) return;
      setDesktopMenuOpen(false);
      setDesktopLangOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setDesktopMenuOpen(false);
    };
    window.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [desktopMenuOpen]);

  useEffect(() => {
    if (!desktopMenuOpen) setDesktopLangOpen(false);
  }, [desktopMenuOpen]);

  useEffect(() => {
    if (!guestLangOpen) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (guestLangRef.current?.contains(target)) return;
      setGuestLangOpen(false);
    };
    window.addEventListener('pointerdown', onPointerDown);
    return () => {
      window.removeEventListener('pointerdown', onPointerDown);
    };
  }, [guestLangOpen]);

  const currentLang = (i18n.language?.slice(0, 2) as SupportedLanguage) ?? 'en';
  const languageLabels: Record<SupportedLanguage, string> = {
    en: t('common.english'),
    pl: t('common.polish'),
  };
  useEffect(() => {
    if (variant !== 'landing') return;
    const onScroll = () => {
      setLandingScrolled(window.scrollY > 12);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [variant]);

  const initials =
    user?.name?.trim()?.[0]?.toUpperCase() ??
    user?.email?.trim()?.[0]?.toUpperCase() ??
    '?';

  const isLanding = variant === 'landing';
  const { handleLandingSectionClick, handleLandingLogoClick } = useLandingSectionNav();

  const landingNavLinks = [
    { to: '/how-it-works', label: t('landing.nav.howItWorks') },
    { to: '/features', label: t('landing.nav.features') },
    { to: '/pricing', label: t('landing.nav.pricing') },
    { to: '/faq', label: t('landing.nav.faq') },
  ] as const;
  const onGuestPickLanguage = (code: SupportedLanguage) => {
    setLanguage(code);
    setGuestLangOpen(false);
  };

  return (
    <>
    <header
      ref={headerRef}
      className={cn(
        'z-50 w-full max-lg:will-change-[background-color,border-color]',
        isLanding &&
          cn(
            'fixed top-0 left-0 right-0 h-[60px] border-b transition-[backdrop-filter] duration-200 ease-out md:h-[72px]',
            landingScrolled
              ? 'border-white/15 bg-black/50 backdrop-blur-xl supports-[backdrop-filter]:bg-black/35'
              : 'border-white/10 bg-transparent',
          ),
        !isLanding &&
          cn(
            'sticky top-0 h-16 bg-ig-bg-primary/90 backdrop-blur-md supports-[backdrop-filter]:bg-ig-bg-primary/75',
            borderless ? 'border-b-0' : appBorderClassName ?? 'border-b border-ig-border',
          ),
      )}
    >
      <div className="mx-auto flex h-full w-full max-w-[1200px] items-center justify-between gap-4 px-5 md:px-8">
        <Link
          to="/"
          onClick={isLanding ? handleLandingLogoClick : undefined}
          className={cn(
            'flex shrink-0 items-center gap-3 transition-colors',
            isLanding ? 'text-white' : 'text-ig-text-primary',
          )}
        >
          <GatorMark className="h-8 w-8 shrink-0 rounded-md" alt="" />
          <span
            className={cn(
              MOBILE_NAV_LOGO_TEXT,
              isLanding ? 'text-white' : undefined,
            )}
          >
            InterviewGator
          </span>
        </Link>

        {isLanding ? (
          <nav
            className={cn('hidden min-w-0 flex-1 justify-center gap-6 lg:flex xl:gap-8', mobileMenuOpen && 'lg:hidden')}
            aria-label={t('landing.nav.pageSections')}
          >
            {landingNavLinks.map(({ to, label }) => {
              const sectionId = landingPathToSectionId(to);
              return (
                <a
                  key={to}
                  href={sectionId ? `#${sectionId}` : to}
                  onClick={(event) => handleLandingSectionClick(event, to)}
                  className="whitespace-nowrap font-sans text-sm font-normal text-white/[0.8] transition hover:text-white"
                >
                  {label}
                </a>
              );
            })}
          </nav>
        ) : (
          <div className="min-w-0 flex-1" />
        )}

        <div className={cn('hidden shrink-0 items-center gap-3 lg:flex', mobileMenuOpen && 'lg:hidden')}>
          {user ? (
            <div className="relative flex items-center gap-3" ref={desktopMenuRef}>
              {showDashboardLink ? (
                <Link
                  to="/dashboard"
                  className="inline-flex h-9 items-center rounded-lg bg-ig-accent px-4 font-sans text-sm font-bold text-[var(--ig-accent-text)] transition hover:brightness-110"
                >
                  {t('nav.dashboard')}
                </Link>
              ) : null}
              <button
                type="button"
                className={cn(
                  'flex h-9 w-9 items-center justify-center overflow-hidden rounded-full border text-xs font-medium transition hover:border-ig-border-strong',
                  isLanding
                    ? 'border-white/25 bg-white/10 text-white'
                    : 'border-ig-border bg-ig-bg-tertiary text-ig-text-primary',
                )}
                aria-expanded={desktopMenuOpen}
                aria-haspopup="menu"
                onClick={() => setDesktopMenuOpen((open) => !open)}
              >
                {user.avatar ? (
                  <img src={resolveAvatarSrc(user.avatar)} alt="" className="h-full w-full object-cover" />
                ) : (
                  initials
                )}
              </button>

              {desktopMenuOpen ? (
                <div
                  role="menu"
                  className={cn(
                    'absolute top-full right-0 z-50 mt-2 w-[260px] overflow-hidden rounded-md border shadow-[0_16px_48px_rgba(0,0,0,0.55)]',
                    isLanding ? 'border-white/15 bg-ig-bg-secondary' : 'border-ig-border-strong bg-ig-bg-elevated',
                  )}
                >
                  <div
                    className={cn(
                      'flex items-center gap-3 px-4 py-3.5',
                      isLanding ? 'border-b border-white/10' : 'border-b border-ig-border',
                    )}
                  >
                    <div
                      className={cn(
                        'flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full border text-[13px] font-medium',
                        isLanding
                          ? 'border-white/15 bg-white/10 text-white'
                          : 'border-ig-border bg-ig-bg-tertiary text-ig-text-primary',
                      )}
                    >
                      {user.avatar ? (
                        <img src={resolveAvatarSrc(user.avatar)} alt="" className="h-full w-full object-cover" />
                      ) : (
                        initials
                      )}
                    </div>
                    <div className="flex min-w-0 flex-col">
                      <span
                        className={cn(
                          'truncate font-sans text-[14px] font-medium leading-tight',
                          isLanding ? 'text-white' : 'text-ig-text-primary',
                        )}
                      >
                        {user.name?.trim() || user.email}
                      </span>
                      {user.name?.trim() ? (
                        <span
                          className={cn(
                            'mt-0.5 truncate font-sans text-[12px] leading-tight',
                            isLanding ? 'text-white/60' : 'text-ig-text-tertiary',
                          )}
                        >
                          {user.email}
                        </span>
                      ) : null}
                    </div>
                  </div>

                  {!restricted ? (
                    <>
                      <div className="flex flex-col p-1.5">
                        {user.role === 'ADMIN' ? (
                          <MenuItem
                            to="/admin"
                            iconName="shield"
                            label={t('admin.pageTitle', { defaultValue: 'Admin' })}
                            isLanding={isLanding}
                            onSelect={() => setDesktopMenuOpen(false)}
                          />
                        ) : null}
                        {user.isCompany ? (
                          <MenuItem
                            to="/company"
                            iconName="briefcase-business"
                            label={t('nav.companyPanel', { defaultValue: 'Company panel' })}
                            isLanding={isLanding}
                            onSelect={() => setDesktopMenuOpen(false)}
                          />
                        ) : null}
                        <MenuItem
                          to="/settings/profile"
                          iconName="user"
                          label={t('nav.profile')}
                          isLanding={isLanding}
                          onSelect={() => setDesktopMenuOpen(false)}
                        />
                        <MenuItem
                          to="/settings"
                          iconName="settings"
                          label={t('nav.settings')}
                          isLanding={isLanding}
                          onSelect={() => setDesktopMenuOpen(false)}
                        />
                        <MenuItem
                          to="/settings/plan"
                          iconName="credit-card"
                          label={t('nav.myPack')}
                          isLanding={isLanding}
                          onSelect={() => setDesktopMenuOpen(false)}
                        />
                      </div>

                      <div className={cn('h-px', isLanding ? 'bg-white/10' : 'bg-ig-border')} />
                    </>
                  ) : null}

                  <div className="flex flex-col p-1.5">
                    <button
                      type="button"
                      role="menuitem"
                      aria-expanded={desktopLangOpen}
                      onClick={() => setDesktopLangOpen((open) => !open)}
                      className={cn(
                        'flex h-10 items-center gap-2.5 rounded-md px-2.5 text-left font-sans text-[14px] transition-colors',
                        isLanding
                          ? 'text-white/90 hover:bg-white/[0.06] hover:text-white'
                          : 'text-ig-text-primary hover:bg-ig-bg-tertiary',
                      )}
                    >
                      <Icon
                        name="globe"
                        className={cn('h-4 w-4 shrink-0', isLanding ? 'text-white/70' : 'text-ig-text-secondary')}
                      />
                      <span className="flex-1">{t('nav.language')}</span>
                      <span className={cn('font-sans text-[12px]', isLanding ? 'text-white/60' : 'text-ig-text-tertiary')}>
                        {languageLabels[currentLang]}
                      </span>
                    </button>
                    {desktopLangOpen ? (
                      <div className="mt-0.5 flex flex-col">
                        {SUPPORTED_LANGUAGES.map((code) => {
                          const selected = code === currentLang;
                          return (
                            <button
                              key={code}
                              type="button"
                              role="menuitemradio"
                              aria-checked={selected}
                              onClick={() => {
                                void setDisplayLanguage(code);
                                setDesktopLangOpen(false);
                              }}
                              className={cn(
                                'flex h-9 items-center gap-2.5 rounded-md pl-9 pr-2.5 text-left font-sans text-[13px] transition-colors',
                                isLanding
                                  ? selected
                                    ? 'bg-white/[0.08] text-white'
                                    : 'text-white/80 hover:bg-white/[0.06] hover:text-white'
                                  : selected
                                    ? 'bg-ig-bg-tertiary text-ig-text-primary'
                                    : 'text-ig-text-primary hover:bg-ig-bg-tertiary',
                              )}
                            >
                              <span className="flex-1">{languageLabels[code]}</span>
                              {selected ? (
                                <Icon name="check" className="h-3.5 w-3.5 text-ig-accent" strokeWidth={2.5} />
                              ) : null}
                            </button>
                          );
                        })}
                      </div>
                    ) : null}
                  </div>

                  <div className={cn('h-px', isLanding ? 'bg-white/10' : 'bg-ig-border')} />

                  <div className="flex flex-col p-1.5">
                    <button
                      type="button"
                      role="menuitem"
                      className="flex h-10 items-center gap-2.5 rounded-md px-2.5 text-left font-sans text-[14px] font-medium text-ig-danger transition-colors hover:bg-ig-bg-tertiary hover:text-ig-danger"
                      onClick={() => {
                        setDesktopMenuOpen(false);
                        void signOut().then(() => navigate('/'));
                      }}
                    >
                      <Icon name="log-out" className="h-4 w-4 shrink-0" />
                      {t('nav.signOut')}
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          ) : isLanding ? (
            <>
              <div className="relative" ref={guestLangRef}>
                <button
                  type="button"
                  aria-expanded={guestLangOpen}
                  aria-haspopup="menu"
                  aria-label={t('landing.nav.changeLanguage')}
                  onClick={() => setGuestLangOpen((open) => !open)}
                  className="inline-flex h-9 items-center gap-2 rounded-lg border border-white/25 bg-transparent px-3 font-sans text-sm font-medium text-white/90 transition hover:border-white/50 hover:text-white"
                >
                  <Icon name="globe" className="h-4 w-4" aria-hidden />
                  <span className="uppercase tracking-wide">{currentLang}</span>
                </button>
                {guestLangOpen ? (
                  <div
                    role="menu"
                    className="absolute top-full right-0 z-50 mt-2 w-[180px] overflow-hidden rounded-lg border border-white/15 bg-ig-bg-secondary p-1.5 shadow-[0_16px_48px_rgba(0,0,0,0.55)]"
                  >
                    {SUPPORTED_LANGUAGES.map((code) => {
                      const selected = code === currentLang;
                      return (
                        <button
                          key={code}
                          type="button"
                          role="menuitemradio"
                          aria-checked={selected}
                          onClick={() => onGuestPickLanguage(code)}
                          className={cn(
                            'flex h-9 w-full items-center gap-2.5 rounded-md px-2.5 text-left font-sans text-[13px] transition-colors',
                            selected
                              ? 'bg-white/[0.08] text-white'
                              : 'text-white/80 hover:bg-white/[0.06] hover:text-white',
                          )}
                        >
                          <span className="flex-1">{languageLabels[code]}</span>
                          {selected ? (
                            <Icon name="check" className="h-3.5 w-3.5 text-ig-accent" strokeWidth={2.5} />
                          ) : null}
                        </button>
                      );
                    })}
                  </div>
                ) : null}
              </div>
              <Link
                to="/login"
                className="inline-flex h-9 items-center rounded-lg border border-white/70 bg-transparent px-4 font-sans text-sm font-normal text-white transition hover:border-white hover:bg-white/5"
              >
                {t('nav.login')}
              </Link>
              <Link
                to="/register"
                className="inline-flex h-9 items-center rounded-lg bg-ig-accent px-4 font-sans text-sm font-bold text-[var(--ig-accent-text)] transition hover:brightness-110"
              >
                {t('nav.signUp')} →
              </Link>
            </>
          ) : hideGuestAuthButtons ? (
            showGuestLanguageButton ? (
              <div className="relative" ref={guestLangRef}>
                <button
                  type="button"
                  aria-expanded={guestLangOpen}
                  aria-haspopup="menu"
                  aria-label={t('landing.nav.changeLanguage')}
                  onClick={() => setGuestLangOpen((open) => !open)}
                  className="inline-flex h-9 items-center gap-2 rounded-lg border border-white/25 bg-transparent px-3 font-sans text-sm font-medium text-white/90 transition hover:border-white/50 hover:text-white"
                >
                  <Icon name="globe" className="h-4 w-4" aria-hidden />
                  <span className="uppercase tracking-wide">{currentLang}</span>
                </button>
                {guestLangOpen ? (
                  <div
                    role="menu"
                    className="absolute top-full right-0 z-50 mt-2 w-[180px] overflow-hidden rounded-lg border border-white/15 bg-ig-bg-secondary p-1.5 shadow-[0_16px_48px_rgba(0,0,0,0.55)]"
                  >
                    {SUPPORTED_LANGUAGES.map((code) => {
                      const selected = code === currentLang;
                      return (
                        <button
                          key={code}
                          type="button"
                          role="menuitemradio"
                          aria-checked={selected}
                          onClick={() => onGuestPickLanguage(code)}
                          className={cn(
                            'flex h-9 w-full items-center gap-2.5 rounded-md px-2.5 text-left font-sans text-[13px] transition-colors',
                            selected
                              ? 'bg-white/[0.08] text-white'
                              : 'text-white/80 hover:bg-white/[0.06] hover:text-white',
                          )}
                        >
                          <span className="flex-1">{languageLabels[code]}</span>
                          {selected ? (
                            <Icon name="check" className="h-3.5 w-3.5 text-ig-accent" strokeWidth={2.5} />
                          ) : null}
                        </button>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            ) : null
          ) : (
            <div className="flex items-center gap-3">
              <Link
                to="/login"
                className="inline-flex h-10 items-center justify-center rounded-md px-4 font-sans text-sm font-medium text-ig-text-secondary transition hover:bg-ig-bg-secondary hover:text-ig-text-primary"
              >
                {t('nav.login')}
              </Link>
              <Link
                to="/register"
                className="inline-flex h-10 items-center justify-center rounded-md bg-ig-accent px-4 font-sans text-sm font-medium text-[var(--ig-accent-text)] transition hover:brightness-110"
              >
                {t('landing.hero.getStarted')}
              </Link>
            </div>
          )}
        </div>

        <div className="lg:hidden">
          <MobileNavToggleButton
            isOpen={mobileMenuOpen}
            tone={mobileMenuOpen || !isLanding ? 'overlay' : 'landing'}
            ariaLabel={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            onClick={() => setMobileMenuOpen((open) => !open)}
          />
        </div>
      </div>
    </header>
    <MobileNavMenu
      open={mobileMenuOpen}
      onClose={() => setMobileMenuOpen(false)}
      variant={variant}
      headerRef={headerRef}
      landingScrolled={landingScrolled}
      hideGuestAuthButtons={hideGuestAuthButtons}
      showGuestLanguageOnly={showGuestLanguageButton}
      restricted={restricted}
    />
    </>
  );
}

function MenuItem({
  to,
  iconName,
  label,
  isLanding,
  onSelect,
}: {
  to: string;
  iconName: IconName;
  label: string;
  isLanding: boolean;
  onSelect: () => void;
}) {
  return (
    <Link
      role="menuitem"
      to={to}
      onClick={onSelect}
      className={cn(
        'flex h-10 items-center gap-2.5 rounded-md px-2.5 font-sans text-[14px] transition-colors',
        isLanding
          ? 'text-white/90 hover:bg-white/[0.06] hover:text-white'
          : 'text-ig-text-primary hover:bg-ig-bg-tertiary',
      )}
    >
      <Icon
        name={iconName}
        className={cn('h-4 w-4 shrink-0', isLanding ? 'text-white/70' : 'text-ig-text-secondary')}
      />
      {label}
    </Link>
  );
}
